import { forwardRef, KeyboardEvent, Ref } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Information } from '@mint-lib/icons';
import {
  Card,
  Chip,
  TruncateText,
  typographyVariants,
} from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

import { useCustomMetricsTypesLabelsByName } from '../../constants/index.js';
import { MetricDraft } from '../../contexts/CustomMetricsContext.jsx';
import { Tooltip } from './Tooltip.jsx';

type CustomMetricCardProps = Readonly<{
  selected: boolean;
  metric: MetricDraft;
  onClick?: () => void;
}>;

export const CustomMetricCard = forwardRef(
  (
    { selected, metric, onClick }: CustomMetricCardProps,
    ref: Ref<HTMLDivElement>,
  ) => {
    const { t } = useTranslation('@myn/import-hub');

    const customMetricsTypesLabelsByName = useCustomMetricsTypesLabelsByName();

    const label = metric.label || t('New name');
    const chipLabel = metric.metricType
      ? customMetricsTypesLabelsByName[metric.metricType]
      : t('Type');

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      if (onClick && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        onClick();
      }
    };

    return (
      <li>
        <Card
          ref={ref}
          role="button"
          tabIndex={0}
          data-dd-action-name="Custom metric"
          onClick={onClick}
          onKeyDown={handleKeyDown}
          selected={selected}
          className="w-full"
          contentClassName="flex items-center justify-between gap-2 p-3 cursor-pointer"
        >
          <TruncateText>
            <h6 className={cn(typographyVariants({ variant: 'h6' }), 'grow')}>
              {label}
            </h6>
          </TruncateText>
          <Chip variant="outline" color="silver">
            {chipLabel}
          </Chip>
          {metric.isUsed && (
            <Tooltip
              description={t('This custom metric is used as a KPI')}
              color="silver"
            >
              <Chip color="silver">
                <Information />
                {t('KPI')}
              </Chip>
            </Tooltip>
          )}
        </Card>
      </li>
    );
  },
);
